/**
 * Fetch-based API client replacing Supabase.
 * All API calls go through this module.
 */

const API_BASE = import.meta.env.VITE_API_URL || "/api";

const TOKEN_KEY = "tailorbook:auth-token";

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: res.statusText }))) as { error?: string };
    throw new Error(body.error || `Request failed: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  async get<T = unknown>(resource: string, action: string, params?: Record<string, string>): Promise<T> {
    const url = new URL(`${API_BASE}/${resource}`, window.location.origin);
    url.searchParams.set("action", action);
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== "") {
          url.searchParams.set(key, value);
        }
      }
    }
    const res = await fetch(url.toString(), {
      method: "GET",
      headers: authHeaders(),
    });
    return handleResponse<T>(res);
  },

  async post<T = unknown>(resource: string, action: string, body?: unknown): Promise<T> {
    const url = new URL(`${API_BASE}/${resource}`, window.location.origin);
    url.searchParams.set("action", action);
    const res = await fetch(url.toString(), {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body ?? {}),
    });
    return handleResponse<T>(res);
  },

  async upload<T = unknown>(resource: string, action: string, formData: FormData): Promise<T> {
    const url = new URL(`${API_BASE}/${resource}`, window.location.origin);
    url.searchParams.set("action", action);
    const token = getAuthToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    // Don't set Content-Type — browser will set multipart boundary
    const res = await fetch(url.toString(), {
      method: "POST",
      headers,
      body: formData,
    });
    return handleResponse<T>(res);
  },
};

/** Build a URL for serving uploaded files */
export function getFileUrl(bucket: string, path: string): string {
  return `${API_BASE}/files?action=serve&bucket=${encodeURIComponent(bucket)}&path=${encodeURIComponent(path)}`;
}
